'use client'

/**
 * BlurHashImg — image avec placeholder BlurHash décodé en canvas.
 *
 * Le canvas affiche la version 32×32 décodée du blurhash (string 30 bytes
 * base83 stockée en DB), avec un filter:blur CSS pour lisser. Quand l'image
 * réelle (props.src) finit de charger, le canvas fade out et l'image fade in.
 *
 * Sensation perçue : "image instantanée" même sur 3G/4G — le visiteur voit
 * IMMÉDIATEMENT une représentation floue colorée fidèle à la photo finale.
 * Pattern utilisé par Unsplash, Spotify, Wolt, Pinterest.
 *
 * Backward-compat : si `blurhash` est absent (items uploadés avant Phase 1),
 * le composant rend juste un fond `background` neutre + l'image.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { decode } from 'blurhash'

interface BlurHashImgProps {
  src: string
  alt: string
  blurhash?: string
  loading?: 'lazy' | 'eager'
  className?: string
  /** Style appliqué au wrapper (position relative obligatoire). */
  style?: CSSProperties
  /** Couleur de fallback si pas de blurhash. Défaut #1a1a1a (proche noir). */
  fallbackBackground?: string
  /** Point focal de l'image (object-position, ex. "50% 30%"). Défaut centre. */
  objectPosition?: string
  /** Remonte la taille naturelle au chargement (pour mesurer la forme réelle). */
  onNaturalSize?: (width: number, height: number) => void
}

export default function BlurHashImg({
  src,
  alt,
  blurhash,
  loading = 'lazy',
  className,
  style,
  fallbackBackground = '#1a1a1a',
  objectPosition = '50% 50%',
  onNaturalSize,
}: BlurHashImgProps) {
  const [loaded, setLoaded] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    if (!blurhash || !canvasRef.current) return
    try {
      const pixels = decode(blurhash, 32, 32)
      const ctx = canvasRef.current.getContext('2d')
      if (!ctx) return
      const imageData = ctx.createImageData(32, 32)
      imageData.data.set(pixels)
      ctx.putImageData(imageData, 0, 0)
    } catch {
      /* BlurHash invalide → on garde le fond fallback. */
    }
  }, [blurhash])

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: blurhash ? undefined : fallbackBackground,
        ...style,
      }}
    >
      {blurhash && (
        <canvas
          ref={canvasRef}
          width={32}
          height={32}
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: loaded ? 0 : 1,
            transition: 'opacity 400ms ease-out',
            filter: 'blur(8px) saturate(1.1)',
            transform: 'scale(1.05)', // cache les bords flous coupés
          }}
        />
      )}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        loading={loading}
        onLoad={(ev) => {
          setLoaded(true)
          const el = ev.currentTarget
          if (onNaturalSize && el.naturalWidth > 0 && el.naturalHeight > 0) {
            onNaturalSize(el.naturalWidth, el.naturalHeight)
          }
        }}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 400ms ease-out',
        }}
      />
    </div>
  )
}
